/**
 * Pure multiply / spread intent for BoardUi fork arrows + candidate ghosts.
 * Mirrors Game.tryMultiply target order and windup (spreadTimer / spreadAfter).
 */

import { canPlaceInSlot, type BoardState } from "./board";
import { LAYER_ORDER, layerInward } from "./types";
import type { LayerId } from "./types";
import type { Presence } from "../cards/types";
import { tiltBand } from "./tiltBands";

/** Match advance intent — below this, no fork. */
export const MULTIPLY_INTENT_SHOW = 0.08;
/** Brighten candidates when commit is near. */
export const MULTIPLY_INTENT_IMMINENT = 0.75;

export type MultiplyCandidateKind = "empty" | "clash";

export interface MultiplyCandidate {
  layer: LayerId;
  slot: number;
  kind: MultiplyCandidateKind;
  /** First legal target in sim order (the one tryMultiply will pick). */
  primary: boolean;
}

export interface MultiplyIntent {
  presenceId: string;
  fromLayer: LayerId;
  fromSlot: number;
  /** 0..1 windup from spreadTimer / spreadAfter */
  progress: number;
  candidates: MultiplyCandidate[];
}

/** Side-slot search order used by tryMultiply (adjacent first, then fill). */
export function multiplySideOrder(fromSlot: number): number[] {
  return [fromSlot - 1, fromSlot + 1, 0, 1, 2].filter(
    (v, i, a) => v >= 0 && v <= 2 && v !== fromSlot && a.indexOf(v) === i,
  );
}

export function multiplyProgress(presence: Presence): number {
  const need = presence.verbs.spreadAfter;
  if (!need || need <= 0) return 0;
  return Math.max(0, Math.min(1, presence.spreadTimer / need));
}

/**
 * Whether this presence is a multiply lineage root that can still spread.
 * (Does not require current layer tilt — windup may be decaying.)
 */
export function canShowMultiplyWindup(presence: Presence): boolean {
  if (presence.side !== "enemy") return false;
  if (!presence.verbs.spreadAfter) return false;
  if (presence.spreadGen >= 1) return false;
  if (presence.marching) return false;
  if (presence.lieType === "confusion" || presence.defId === "confusion") {
    return false;
  }
  if (presence.lieType === "dogma" || presence.dogma) {
    return false;
  }
  return true;
}

/**
 * Legal spawn cells in tryMultiply priority order (at most one will fire).
 * Same-layer empties win over inward; inward needs corrupted tilt.
 */
export function listMultiplyCandidates(
  board: BoardState,
  layer: LayerId,
  slot: number,
  presence: Presence,
): MultiplyCandidate[] {
  const out: MultiplyCandidate[] = [];

  for (const s of multiplySideOrder(slot)) {
    if (
      !board[layer].slots[s] &&
      canPlaceInSlot(board, layer, s, "enemy")
    ) {
      out.push({
        layer,
        slot: s,
        kind: "empty",
        primary: out.length === 0,
      });
    }
  }

  // Sim only considers inward when no same-layer empty accepted
  if (out.length > 0) return out;

  const inn = layerInward(layer);
  if (!inn) return out;
  if (board[inn].tilt < tiltBand(inn).lieMarch) return out;

  const lane =
    presence.homeSlot >= 0 && presence.homeSlot <= 2
      ? presence.homeSlot
      : slot;
  const occ = board[inn].slots[lane];
  if (!occ && canPlaceInSlot(board, inn, lane, "enemy")) {
    out.push({ layer: inn, slot: lane, kind: "empty", primary: true });
  } else if (occ?.side === "player") {
    out.push({ layer: inn, slot: lane, kind: "clash", primary: true });
  }
  return out;
}

export function resolveMultiplyIntent(
  board: BoardState,
  layer: LayerId,
  slot: number,
  presence: Presence,
): MultiplyIntent | null {
  if (!canShowMultiplyWindup(presence)) return null;
  const progress = multiplyProgress(presence);
  if (progress < MULTIPLY_INTENT_SHOW) return null;

  const candidates = listMultiplyCandidates(board, layer, slot, presence);
  // Still show windup with no legal cell (bar / parent pulse); empty forks.
  return {
    presenceId: presence.instanceId,
    fromLayer: layer,
    fromSlot: slot,
    progress,
    candidates,
  };
}

export function collectMultiplyIntents(board: BoardState): MultiplyIntent[] {
  const out: MultiplyIntent[] = [];
  for (const id of LAYER_ORDER) {
    board[id].slots.forEach((p, slot) => {
      if (!p) return;
      const intent = resolveMultiplyIntent(board, id, slot, p);
      if (intent) out.push(intent);
    });
  }
  return out;
}
